// Drug label HTML, as a pure string builder.
//
// Called by BOTH the Print Drug Label dialog and the live preview in Drug Label
// Settings — that is the point. If the preview used different code, a clinic
// would discover the difference on a roll of misprinted labels.
//
// Dependencies come in as arguments rather than imports so this stays pure and
// portable: print_lang(key, fallback), currentLanguage, apiUrl, autoPrint,
// translations (optional — the raw { key: { en, th } } map; when present,
// every translated word resolves against the BRANCH's language instead of
// print_lang's operator-toggle binding — see the `t` construction below).
//
// TWO SOURCES OF WORDS, and they are not interchangeable:
//   * `t()` / app_lang — the label's own FURNITURE ("Directions:", "for",
//     "days", "Drug allergy", the meal names). Fixed UI strings with real
//     app_lang keys.
//   * the payload's *_label_en / *_label_th pairs — CLINICAL DATA the API
//     resolved from the table that owns it (medication_frequencies,
//     medication_routes). Resolved with `pickLabel` below, never with `t()`.
// Routing clinical data through `t()` is what put the raw code "tid" on a
// patient's medicine bag: app_lang has no row for any dosing code, so the
// lookup returned the key. If a new coded field ever needs printing, the API
// joins its lookup table and sends the pair — it does not get an app_lang row
// named after a database code.
//
// Templates:
//   A "Standard" — Rx number, DN + patient block, directions WITH duration,
//                  clinic phone in the footer.
//   B "Compact"  — no Rx number, no duration; phone and mobile in the header,
//                  the date under the DN, larger base font.
//
// Document order is safety-ordered, not just visual: the drug allergy warning
// sits immediately after the patient/medication identification block, above
// every discardable block (directions, food timing, note, meta, footer) —
// see "Fix round 1, F1" below. If a label still doesn't fit after shrinking,
// it is the address/timestamp that clips, never the allergy line.
//
// Fix round 1, F1: realistic content (compound drug name, multi-item allergy
// list, long instructions, all four meal tokens) can exceed the configured
// label box. Rather than let `overflow: hidden` silently discard content —
// which, before this fix, discarded the allergy warning first because it was
// last in document order — this module now (a) orders the allergy warning
// right after the identification block, and (b) runs a deterministic
// shrink-to-fit BEFORE rendering, reducing the base font size in fixed steps
// until the estimated content fits the box, or a legibility floor is hit.
// That estimate is a character/line-count model, not a browser measurement,
// specifically so the settings preview and the print dialog — both callers
// of this one function — always agree: same input, same shrink, same output,
// byte for byte. See `data-label-overflow` / `data-label-font-size` on the
// returned document's <html> tag: Task 6's preview reads these to warn the
// operator when even the floor size does not fit.
//
// Fix round 2, F1a/F1b/F1c: the estimator's "fits" verdict must never be
// optimistic, because Task 6 renders it to the operator as a literal
// pass/fail signal. Three corrections: (a) `estimateLines()` assumes text
// wraps at any character, so every free-text block now carries
// `overflow-wrap: anywhere` in the stylesheet to make that assumption true
// in a real browser too — otherwise a spaceless run (e.g. a comma-separated
// allergy list with no spaces) doesn't wrap at all and clips horizontally
// while being scored as "fits". (b) `.med` and `.allergy` render bold, and
// bold glyphs are measurably wider, so they're estimated with a wider
// per-character factor than regular text; the fit check also now requires
// headroom (a margin below the true usable height), so a result that only
// clears by ~1% shrinks one more step instead of squeaking through — that
// margin is exactly where the model's own known imprecision lives. (c) the
// floor is now an unconditional clamp, not just a loop-exit condition, so a
// stored `font_size` already below the floor can't pass through unshrunk.

import { foodTimingLabel, mealTimesLabel } from './foodTiming';
// Sarabun's @font-face rules with the woff2 bytes inlined as data: URIs. This
// replaced a <link href="fonts.googleapis.com/..."> in the emitted document.
// estimateLines() below assumes Sarabun's metrics, so pulling the face over
// the network at print time made the deterministic shrink-to-fit depend on
// that fetch landing inside the 350ms (browser) / 400ms (agent) window before
// printing. On an offline or slow clinic PC it did not: the label printed in a
// fallback face whose glyphs are a different width, while the fit signal still
// reported the Sarabun answer — and the stylesheet was render-blocking, so an
// unreachable host stalled the print as well. Inlined, the document is
// self-contained and both print paths get identical metrics with no network.
import { SARABUN_FONT_FACE_CSS } from './sarabunFont';

// Everything that reaches the page is escaped. Patient names, medication names
// and clinic addresses are user-entered and would otherwise break the document.
function esc(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function generateDrugLabelHTML(labelData, {
  print_lang, currentLanguage, apiUrl, autoPrint = true, translations,
} = {}) {
  const settings = labelData.settings || {};
  const patient = labelData.patient || {};
  const med = labelData.medication || {};
  const dose = labelData.dosage || {};
  const rx = labelData.prescription || {};
  const branch = labelData.branch || {};
  const copies = Math.max(1, Number(labelData.copies) || 1);

  const template = settings.template === 'B' ? 'B' : 'A';
  // Labels render in the BRANCH's language, not the operator's UI toggle, so a
  // label reads the same whoever prints it. currentLanguage is only a fallback
  // for a branch with no language configured.
  const lang = branch.language || currentLanguage || 'th';
  const locale = lang === 'th' ? 'th-TH' : 'en-GB';

  // Word-level strings must follow the same branch-language rule as `lang`
  // above, not the operator's UI toggle. `print_lang` (from LanguageContext)
  // is bound to the operator's toggle, so it is only a fallback for a caller
  // that doesn't pass the raw `translations` map. When `translations` IS
  // supplied, resolve every string against `lang` (already branch-first)
  // instead — this is why `lang` must be computed above before `t` is built.
  const t = translations
    ? (key, fallback) => {
      const row = translations[key];
      if (!row) return fallback;
      return (lang === 'th' ? row.th : row.en) || fallback;
    }
    : (print_lang || ((key, fallback) => fallback));

  const w = Number(settings.label_width_mm) || 63.5;
  const h = Number(settings.label_height_mm) || 38.1;
  const mt = Number(settings.margin_top ?? 2);
  const mb = Number(settings.margin_bottom ?? 2);
  const ml = Number(settings.margin_left ?? 2);
  const mr = Number(settings.margin_right ?? 2);
  const family = settings.font_family || 'Sarabun';

  const fmtDate = (d) => {
    if (!d) return '';
    const parsed = new Date(d);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString(locale);
  };

  // Words the API resolved from a lookup table (medication_frequencies,
  // medication_routes) arrive as an en/th pair. Pick by the label's language —
  // the BRANCH's, resolved above — preferring the other language over nothing,
  // and only falling back to `raw` when neither exists.
  //
  // This exists because `t()` is an app_lang lookup keyed by UI translation
  // key, and the dosing frequency is not a UI string: it is stored as a code
  // ('tid', 'q8h'), app_lang has no row for any of the ten codes, so `t('tid',
  // 'tid')` returned "tid" and the raw database code printed on the patient's
  // medicine bag where "Three times daily" / "วันละ 3 ครั้ง" belongs. The fix
  // is to read the table that owns the words, not to seed app_lang with rows
  // named after database codes.
  const pickLabel = (en, th, raw) =>
    (lang === 'th' ? (th || en) : (en || th)) || raw || '';

  const frequencyText = pickLabel(dose.frequency_label_en, dose.frequency_label_th, dose.frequency);
  const routeText = pickLabel(dose.route_label_en, dose.route_label_th, dose.route);

  // `medication.unit` ('cap', 'tablet', 'ml') is free text an admin typed into
  // medications.unit — there is no lookup table for it, so there is nothing to
  // resolve it against. It used to go through t(unit.toLowerCase(), unit),
  // which is the same improvised app_lang lookup as the frequency one: it hit
  // by coincidence for the handful of units that happen to collide with a UI
  // key ('tablet', 'capsule', 'unit') and passed everything else through raw —
  // so the same clinic printed "เม็ด" for one medication and "cap" for the
  // next, and a unit typed as "unit" rendered as the UI word "หน่วย". Printed
  // verbatim now, exactly like the medication name and strength beside it.
  const unitText = med.unit ? esc(med.unit) : '';

  // Directions: route, dose × unit, frequency, and — template A only —
  // duration. Falls back to the free-text instructions for a line without
  // structured fields. Escaped at construction (every push is esc()'d
  // individually) so `directions` is already safe to interpolate directly
  // wherever it's used.
  const dirParts = [];
  // Route leads the line: on a bag, "how" comes before "how much" in both
  // languages ("รับประทาน 1 cap วันละ 3 ครั้ง" / "Oral 1 cap Three times
  // daily"), and the oral/topical distinction is the one a patient most needs
  // to get right.
  if (routeText) dirParts.push(esc(routeText));
  if (dose.dose_per_time) {
    dirParts.push(`${esc(dose.dose_per_time)}${unitText ? ` ${unitText}` : ''}`);
  }
  if (frequencyText) dirParts.push(esc(frequencyText));
  if (template !== 'B' && dose.duration_days) {
    dirParts.push(`${esc(t('for_time', 'for'))} ${esc(dose.duration_days)} ${esc(t('days', 'days'))}`);
  }
  const directions = dirParts.length
    ? dirParts.join(' ')
    : esc(dose.dosage_instructions || '');

  // Food timing then meals, e.g. "Take after food  Morning, Evening".
  // foodTimingLabel preserves after_food === 2; never test it as a boolean.
  // Escaped at construction — do not wrap `foodLine` in esc() again at its
  // use site, and if it's ever reused elsewhere, reuse it as-is.
  const foodLine = [
    esc(foodTimingLabel(dose.before_food, dose.after_food, t, 'take')),
    esc(mealTimesLabel(dose.meal_times, t)),
  ].filter(Boolean).join(' ');

  // Escaped at construction — safe to interpolate directly at every use site
  // below (including the shrink-to-fit estimate further down).
  const medTitle = esc([med.name, med.strength].filter(Boolean).join(' '));
  const quantityText = dose.quantity
    ? `${esc(dose.quantity)}${unitText ? ` ${unitText}` : ''}`
    : '';
  const medLine = medTitle + quantityText;

  // The DN line is omitted entirely when the patient has none — no placeholder,
  // and template B's date moves up into the freed space.
  const dnHtml = patient.dn
    ? `<span class="dn">${esc(patient.dn)}</span>`
    : '';

  const rxLineHtml = template === 'B' || !rx.number
    ? ''
    : `<div class="rx">${esc(t('rx_no', 'Rx No.'))} ${esc(rx.number)}</div>`;

  const headerPhone = [branch.phone, branch.mobile_phone].filter(Boolean).join(' / ');
  const headerPhoneHtml = template === 'B' && headerPhone
    ? `<div class="hdr-phone">${esc(headerPhone)}</div>` : '';
  const footerPhoneHtml = template === 'A' && branch.phone
    ? `<div class="footer">${esc(branch.phone)}</div>` : '';

  const logoHtml = settings.include_logo && branch.logo_path && apiUrl
    ? `<img class="logo" src="${esc(apiUrl)}/${esc(branch.logo_path)}" alt="" />`
    : '';

  // Escaped at construction as ONE composed string. The ": " separator has no
  // characters esc() would touch, so escaping the whole line here is
  // equivalent to escaping the label and the value separately, but leaves a
  // single safe intermediate (`allergyPlain`) instead of two fragments that a
  // future edit could recombine unescaped.
  const allergyPlain = patient.drug_allergy
    ? `${t('drug_allergy', 'Drug allergy')}: ${patient.drug_allergy}`
    : '';
  const allergyHtml = allergyPlain
    ? `<div class="allergy">${esc(allergyPlain)}</div>`
    : '';

  // Escaped at construction.
  const notePlain = dose.special_instructions ? esc(dose.special_instructions) : '';
  const noteHtml = notePlain ? `<div class="note">${notePlain}</div>` : '';

  // Escaped at construction — reused at two use sites below (template B's
  // patient row and template A's meta line); neither needs to esc() again.
  const dateLabel = esc(rx.dispensed_date ? fmtDate(rx.dispensed_date) : fmtDate(rx.date));

  // --- Deterministic shrink-to-fit ------------------------------------
  // Fix round 1, F1: estimate whether the content fits the physical label
  // box and, if not, shrink the base font size in fixed steps until it does
  // (or a legibility floor is reached). This MUST live here, inside the pure
  // generator, and MUST NOT be done by measuring the rendered DOM in a
  // browser — the settings preview (Task 6) and the print dialog (Task 7)
  // both call this one function, and the whole point of the module is that
  // they get byte-identical output for byte-identical input. A real browser
  // layout pass can't be guaranteed to agree between the two call sites (a
  // preview iframe vs. a print window can differ in available fonts,
  // sub-pixel rounding, zoom, etc.); a character/line estimate computed once
  // in plain JS is deterministic by construction, so it doesn't need to be
  // pixel-exact — only the same on both paths, which it is, because it's the
  // same code.
  const MM_PER_PX = 25.4 / 96; // CSS px -> mm at 96dpi
  const AVG_CHAR_WIDTH_FACTOR = 0.55; // rough average glyph width vs. font size, regular weight
  const BOLD_CHAR_WIDTH_FACTOR = 0.62; // bold glyphs (.med, .allergy) run measurably wider
  const LINE_HEIGHT_FACTOR = 1.25; // matches the printed CSS line-height
  const MIN_FONT_PX = 6; // floor: below this a label is not legible
  const SHRINK_STEP_PX = 0.5;
  // Headroom: require the estimate to clear the box by a margin, not just
  // squeak under it. A result that only clears by ~1% leaves no room for the
  // model's own known imprecision (bold-weight width, sub-mm rounding), so
  // it's treated as a non-fit and shrinks one more step.
  const HEADROOM_FACTOR = 0.93;

  const usableWidthMm = Math.max(5, w - ml - mr);
  const usableHeightMm = Math.max(5, h - mt - mb);
  const fitHeightMm = usableHeightMm * HEADROOM_FACTOR;

  // Estimated wrapped-line count for a plain-text string at a given font
  // size. Escaped entities (&amp; etc.) only make this estimate slightly
  // more conservative (they read as more characters than they'll render as),
  // which is the safe direction to be wrong in. `bold` selects the wider
  // per-character factor for .med/.allergy; pairs with `overflow-wrap:
  // anywhere` in the stylesheet below, which is what makes "wraps at any
  // character" actually true in a real browser instead of just in this model.
  function estimateLines(text, sizePx, bold = false) {
    if (!text) return 0;
    const fontMm = sizePx * MM_PER_PX;
    const charWidthFactor = bold ? BOLD_CHAR_WIDTH_FACTOR : AVG_CHAR_WIDTH_FACTOR;
    const charsPerLine = Math.max(1, Math.floor(usableWidthMm / (fontMm * charWidthFactor)));
    return Math.max(1, Math.ceil(text.length / charsPerLine));
  }
  function lineHeightMm(sizePx) {
    return sizePx * MM_PER_PX * LINE_HEIGHT_FACTOR;
  }

  // Fixed single-line rows (header, patient identification, Rx line, meta,
  // footer) plus the wrap-estimated free-text rows that realistically run
  // long (medication name+qty, allergy warning, directions, food/meal line,
  // special note). Point-size deltas mirror the <style> block below so the
  // estimate tracks what actually prints; `.med`/`.allergy` are bold there
  // and are estimated with the bold width factor here.
  function estimateContentHeightMm(basePx) {
    let mm = 0;
    mm += lineHeightMm(basePx + 1); // head: clinic name row
    mm += lineHeightMm(basePx); // patient identification row
    if (rxLineHtml) mm += lineHeightMm(basePx - 2);
    mm += estimateLines(medLine, basePx + 2, true) * lineHeightMm(basePx + 2);
    if (allergyPlain) mm += estimateLines(allergyPlain, basePx - 1, true) * lineHeightMm(basePx - 1);
    if (directions) {
      const dirPx = template === 'B' ? basePx + 1 : basePx;
      mm += estimateLines(directions, dirPx) * lineHeightMm(dirPx);
    }
    // `.food` renders at font-weight 600. Semibold sits between the regular and
    // bold width factors, and the only safe direction for this estimate to be
    // wrong is wider — under-estimating is what lets a line clip while the
    // signal still reports "fits" — so it is scored with the bold factor.
    if (foodLine) mm += estimateLines(foodLine, basePx, true) * lineHeightMm(basePx);
    if (notePlain) mm += estimateLines(notePlain, basePx - 1) * lineHeightMm(basePx - 1);
    if (template === 'A') mm += lineHeightMm(basePx - 2); // meta
    if (footerPhoneHtml) mm += lineHeightMm(basePx - 2); // footer
    return mm;
  }

  const requestedFs = Number(settings.font_size) || 10;
  let fs = requestedFs;
  while (fs > MIN_FONT_PX && estimateContentHeightMm(fs) > fitHeightMm) {
    fs = Math.max(MIN_FONT_PX, Number((fs - SHRINK_STEP_PX).toFixed(1)));
  }
  // F1c: an unconditional clamp, not just a loop-exit condition — a stored
  // `settings.font_size` already at or below the floor must still end up at
  // the floor rather than passing through unshrunk (the while loop above
  // never runs in that case, since its guard is the same `fs > MIN_FONT_PX`).
  fs = Math.max(MIN_FONT_PX, fs);
  // Mechanism for "still doesn't fit even at the floor": exposed as
  // data-label-overflow ("true"/"false") and data-label-font-size (the
  // effective, possibly-shrunk size actually used) on the returned
  // document's <html> element. Callers can detect this either by a plain
  // substring/regex check on the returned HTML string, or — once the HTML is
  // mounted in an iframe for preview — via
  // `iframe.contentDocument.documentElement.dataset.labelOverflow`. Task 6's
  // settings preview should read this to warn the operator to choose a
  // larger label; Task 7's print dialog may use it to log/flag the print.
  // overflow:hidden on `.label` remains as the last-resort visual backstop
  // for this case, since there's nowhere left to shrink to.
  const stillOverflows = estimateContentHeightMm(fs) > fitHeightMm;

  const oneLabel = `
      <div class="label tpl-${template}">
        <div class="head">
          ${logoHtml}
          <div class="clinic">${esc(branch.name || '')}</div>
          ${headerPhoneHtml}
        </div>
        <div class="patient">
          ${dnHtml}
          <span class="pname">${esc(patient.name || patient.name_en || '')}</span>
          ${template === 'B' ? `<span class="pdate">${dateLabel}</span>` : ''}
        </div>
        ${rxLineHtml}
        <div class="med">${medTitle}${quantityText ? ` <span class="qty">${quantityText}</span>` : ''}</div>
        ${allergyHtml}
        ${directions ? `<div class="dir"><span class="dir-label">${esc(t('directions', 'Directions'))}:</span> ${directions}</div>` : ''}
        ${foodLine ? `<div class="food">${foodLine}</div>` : ''}
        ${noteHtml}
        ${template === 'A' ? `<div class="meta">${dateLabel}${rx.dentist_name ? ` · ${esc(rx.dentist_name)}` : ''}</div>` : ''}
        ${footerPhoneHtml}
      </div>`;

  return `<!DOCTYPE html>
<html lang="${esc(lang)}" data-label-overflow="${stillOverflows ? 'true' : 'false'}" data-label-font-size="${fs}">
<head>
<meta charset="utf-8" />
<title>${esc(t('drug_label', 'Drug Label'))}</title>
<style>
  /* Sarabun, embedded — see utils/sarabunFont.js */
${SARABUN_FONT_FACE_CSS}
  @page { size: ${w}mm ${h}mm; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: '${esc(family)}', 'Sarabun', sans-serif; }
  .label {
    width: ${w}mm; height: ${h}mm;
    padding: ${mt}mm ${mr}mm ${mb}mm ${ml}mm;
    font-size: ${fs}px; line-height: 1.25;
    overflow: hidden;
    page-break-after: always;
    display: flex; flex-direction: column;
  }
  .label:last-child { page-break-after: auto; }
  .head { display: flex; align-items: center; gap: 1mm; border-bottom: 0.3mm solid #000; padding-bottom: 0.5mm; }
  .logo { height: ${Math.max(3, h * 0.12)}mm; width: auto; }
  .clinic { font-weight: 700; font-size: ${fs + 1}px; flex: 1; }
  .hdr-phone { font-size: ${fs - 2}px; }
  .patient { display: flex; gap: 1.5mm; align-items: baseline; margin-top: 0.6mm; }
  .dn { font-family: monospace; font-weight: 700; }
  .pname { font-weight: 600; flex: 1; }
  .pdate { font-size: ${fs - 2}px; }
  .rx { font-size: ${fs - 2}px; color: #333; }
  /* F1a: nothing else here allows a run of text with no space or hyphen to
     wrap, so the estimator's "wraps at any character" assumption would be
     false without this — a spaceless run (e.g. a comma-packed allergy list)
     would render as one unbreakable line and clip horizontally instead. */
  .med, .dir, .food, .note, .meta, .allergy {
    overflow-wrap: anywhere; word-break: break-word;
  }
  .med { font-weight: 700; font-size: ${fs + 2}px; margin-top: 0.6mm; }
  .qty { font-weight: 400; font-size: ${fs}px; }
  .allergy { color: #b00; font-weight: 700; font-size: ${fs - 1}px; margin-top: 0.4mm; }
  .dir { margin-top: 0.4mm; }
  .dir-label { font-weight: 600; }
  .food { font-weight: 600; }
  .note { font-style: italic; font-size: ${fs - 1}px; }
  .meta { margin-top: auto; font-size: ${fs - 2}px; color: #333; }
  .footer { font-size: ${fs - 2}px; text-align: center; border-top: 0.3mm solid #000; padding-top: 0.4mm; }
  /* Template B trades the Rx line and duration for a larger base size. */
  .tpl-B .med { font-size: ${fs + 3}px; }
  .tpl-B .dir { font-size: ${fs + 1}px; }
</style>
</head>
<body>
${Array.from({ length: copies }, () => oneLabel).join('\n')}
${autoPrint ? '<script>window.addEventListener("load",function(){setTimeout(function(){window.print();},350);});</script>' : ''}
</body>
</html>`;
}

export default generateDrugLabelHTML;
